import type { FieldDefinition } from '@src/utils/fieldDefinitionUtils';

export const otcOptionFieldDefinitions = {
  counterparty: {
    title: 'Counterparty',
    tooltip:
      'The counterparty to the contract. This list contains counterparties enabled for OTC and associated with a Credit Support Annex',
    columnTooltip: 'The counterparty to the contract',
  },
  marketAccount: {
    title: 'Market Account',
    tooltip: 'The Derivatives Market Account for the counterparty',
    columnTooltip: 'The Derivatives Market Account for the counterparty',
  },
  creditSupportAnnex: {
    title: 'Credit Support Annex',
    tooltip: 'The credit support annex to the contract. This list contains those associated with the counterparty.',
    columnTooltip: 'The credit support annex to the contract',
  },
  tradeDate: {
    title: 'Trade Date',
    tooltip: 'Initial booking date for the contract',
  },
  effectiveDate: {
    title: 'Effective Date',
    tooltip: 'Date on which the contract becomes active',
  },
  side: {
    title: 'Side',
    tooltip: 'Traded side of the contract booked in risk',
  },
  unitNotional: {
    title: 'Unit Notional',
    tooltip:
      'The amount of notional for a single unit quantity of this contract. You can then trade any amount when booked.',
  },
  notionalAmount: {
    title: 'Notional',
    tooltip: 'The amount of exposure in quote asset for this contract',
  },
  payoffType: {
    title: 'Payoff Type',
    tooltip:
      'Whether the contract delivers the P&L on the contract in fiat or stablecoins (LINEAR) or in underlying asset (INVERSE)',
  },
  settlementType: {
    title: 'Settlement Type',
    tooltip: 'Whether this is a financial / cash-settled derivative or physically-settled.',
  },
  index: {
    title: 'Index',
    tooltip:
      'The index that is used as a reference price when computing delivery quantities, e.g. BRRNY for BTC underliers',
  },
  underlying: {
    title: 'Underlying',
    tooltip: 'The underlying asset that is referenced for contract P&L, and also delivered for inverses',
  },
  quantity: {
    title: 'Quantity',
    tooltip: 'Number of contracts, in Unit Notional, absolute amount of underlying asset, or HomeCurrency',
  },
  quoteAsset: {
    title: 'Quote Asset',
    tooltip: 'The asset in which we express the price of the underlier',
  },
  deliveryAsset: {
    title: 'Delivery Asset',
    tooltip:
      "The asset that will be delivered at expiration; it may be fiat or the stablecoin equivalent of the asset's current index price if linear",
  },
  optionType: {
    title: 'Option Type',
    tooltip: 'Whether this is a put or a call option',
  },
  exerciseStyle: {
    title: 'Exercise Style',
    tooltip: 'When the option may be exercised, e.g. any time or only at expiry',
  },
  initialMarginPercent: {
    title: 'Initial Margin %',
    tooltip:
      'Override to the default Initial Margin % with this counterparty in case Dealer offers a break or requires a premium on IM for this particular deal.',
  },
  initialMarginCurrency: {
    title: 'Initial Margin Currency',
    tooltip: 'If multiple accepted collateral assets in the CSA, the selected asset to use for margin purposes.',
  },
  haircutPercent: {
    title: 'Haircut %',
    tooltip:
      'Optional override to the haircut applied to the Initial Margin Currency for this deal. If unset, the CSA accepted-collateral haircut is used.',
  },
  maintenanceMarginPercent: {
    title: 'Maintenance Margin %',
    tooltip:
      'For OTC derivatives, % of notional that needs to be maintained as equity to avoid a margin call, e.g. 1% of position value. In auto-liquidation mode, this can be used as a warning threshold, or it can be set equal to Liquidation Margin % to indicate no warning.',
  },
  marginCallPercent: {
    title: 'Margin Call %',
    tooltip:
      'Collateral health % at which a warning-level margin call is triggered to restore sufficient collateral levels. If not specified, margin calls are immediately raised when collateral value is below Maintenance Margin %, and position will be liquidated immediately if not cured.',
  },
  liquidationMarginPercent: {
    title: 'Margin Liquidation %',
    tooltip: 'For OTC derivatives, % of notional that needs to be maintained as equity to avoid auto-liquidation',
  },
  releaseMarginPercent: {
    title: 'Margin Release %',
    tooltip:
      'For OTC derivatives, if equity exceeds this level, the customer may be allowed to withdraw excess collateral',
  },
  initialMarginAmount: {
    title: 'Initial Margin Amount',
    tooltip:
      'Based on notional amount and initial margin % in force, the amount of margin that must be posted, adjusted for any haircuts',
  },
  postedMargin: {
    title: 'Posted Margin',
    tooltip: 'Amount of collateral asset posted as margin for this OTC position',
  },
  initialMarginAmountHomeCcy: {
    title: 'Initial Margin Amount ($HomeCcy)',
    tooltip:
      'Based on notional amount and initial margin % in force, the amount of margin that must be posted in $HomeCurrency, adjusted for any haircuts',
  },
  strikeType: {
    title: 'Strike Type',
    tooltip: 'How strike can be entered: as a fixed price in Quote Asset, e.g. $100,000, or in delta terms, e.g. 25D',
  },
  strikePrice: {
    title: 'Strike Price',
    tooltip:
      'Strike price for the option, whether expressed as a fixed price in Quote Asset, or in delta terms, e.g. 25D',
  },
  deltaValue: {
    title: 'Delta Value',
    tooltip: 'Delta value for the option, e.g. 25D (0-100 exclusive)',
  },
  paymentCcy: {
    title: 'Payment Ccy',
    tooltip: 'Accepted currency for paying the option premium and any fees.',
  },
  premium: {
    title: 'Premium',
    tooltip: 'Premium amount to be paid at the start of the contract',
  },
  commission: {
    title: 'Commission',
    tooltip: 'Commission paid to enter into this contract, as bps on top of the forward price, if any',
  },
  commissionCurrency: {
    title: 'Commission Ccy',
    tooltip: 'Currency accepted for payment of the commission, if any',
  },
  tenor: {
    title: 'Tenor',
    tooltip: 'Relative expiration date (6M, 1Y, etc.)',
  },
  terminationDate: {
    title: 'Termination Date',
    tooltip:
      'In case of early termination (which may incur a fee if allowed), the date on which the option position is fully unwound before Maturity Date',
  },
  expirationDate: {
    title: 'Expiry Date',
    tooltip: 'Expiration date, with corrections for date adjustments',
  },
  settlementCycle: {
    title: 'Settlement Cycle',
    tooltip:
      'Window to post initial margin, deliver at end of contract, as well as the default window to settle any top-up requests for maintenance margin',
  },
  businessDayConvention: {
    title: 'Business Day Convention',
    tooltip: 'Roll convention to use if a contract date falls on a holiday',
  },
  holidayCalendars: {
    title: 'Holiday Calendars',
    tooltip: 'List of business centers (calendars), e.g. NYS, to apply when adjusting dates to business days',
  },
  leg: {
    title: 'Leg',
    tooltip: 'Order for the legs in a multi-leg instrument',
  },
  productTemplate: {
    title: 'Product Template',
    tooltip: 'Product template descriptive symbol',
  },
  suffix: {
    title: 'Suffix',
    tooltip: 'Optional tag to append to symbol, differentiating different product templates',
  },
  productDetails: {
    title: 'OTC Fields',
    tooltip: 'Product economic details for the instrument being created',
  },
} as const satisfies Record<string, FieldDefinition>;

export type OTCOptionFieldKey = keyof typeof otcOptionFieldDefinitions;
